import React, { useEffect, useRef, useState } from "react";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import TuneIcon from "@mui/icons-material/Tune";
import SearchBar from "../../../Subcomponents/Search";
import ActionSheet from "./ActionSheet";
import ActiveFilterTags from "./ActiveFilterTags";
import ConfirmSheet from "./ConfirmSheet";
import FilterSheet from "./FilterSheet";
import FosCard from "./FosCard";
import FosDetail from "./FosDetail";
import PartnerCard from "./PartnerCard";
import PartnerDetail from "./PartnerDetail";
import SortSheet from "./SortSheet";
import useBackToClose from "./useBackToClose";
import { buildActiveFilterTags } from "./partnersMobileUtils";
import "./tokens.css";
import css from "./partnersMobile.module.css";

// Overrides Subcomponents/Search's rem sizing. 16px on the input also stops
// iOS Safari zooming the page when the field takes focus.
const SEARCH_SX = {
  "& input": {
    fontSize: "16px !important",
    padding: "11px 14px 11px 0 !important",
    lineHeight: "22px !important",
  },
  "& .MuiInputAdornment-root": { marginRight: "4px" },
  // The search glyph itself is fine: SearchBar sets it inline at 3rem, which
  // lands at 21px under the mobile root. Inline styles beat sx anyway.
};

export default function PartnersMobile(props) {
  const {
    tabs, tabValue, tabChange,
    cp, fos, totalCount, totalFOSCount,
    onSearch, onSearchFos,
    sortValue, onSort,
    statuses, types, datePreset, customRange,
    onApplyFilters, onClearFilters, onRemoveFilterTag,
    page, pageFos, onPageChange, onPageChangeFos, pageSize,
    onOpenPartner, onPartnerAction, onOpenFos, onFosAction,
    detailRow, detailDocs, onCloseDetail, onApprove, onReject, canApprove, canReject,
    rejectLabel,
    fosDetailRow, fosDetailDocs, onCloseFosDetail,
    actionSheet, onActionSelect, onCloseActionSheet,
    cpLoaded, fosLoaded,
  } = props;

  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  // 'approve' | null — drives ConfirmSheet for the detail page's Approve.
  const [confirmAction, setConfirmAction] = useState(null);

  const isCp = tabValue === "CP";
  // The container's cp/fos arrays accumulate — getCp/getFos are called with
  // merge=true, so every fetched page stays in memory. Showing one page at a
  // time therefore means slicing, exactly as the desktop DataGrid does.
  const allRows = (isCp ? cp : fos) ?? [];
  const total = (isCp ? totalCount : totalFOSCount) ?? 0;
  const size = pageSize || 12;
  const changePage = isCp ? onPageChange : onPageChangeFos;
  const lastPage = Math.max(0, Math.ceil(total / size) - 1);
  // Clamp defensively: if any path ever resets the rows without resetting the
  // page index, an out-of-range page would slice to nothing and sit on the
  // loading state forever.
  const currentPage = Math.min(Math.max((isCp ? page : pageFos) ?? 0, 0), lastPage);

  const rows = allRows.slice(currentPage * size, currentPage * size + size);
  // A page whose rows haven't arrived yet is loading, not empty — don't flash
  // the "no results" state while the fetch is in flight. Also loading: the
  // tab's first fetch hasn't answered yet, or search/sort cleared the rows
  // while the last known total says there are records (a fetch that finds
  // none sets the total to 0, which then shows the empty state).
  const awaitingPage =
    (rows.length === 0 && allRows.length > 0) ||
    !(isCp ? cpLoaded : fosLoaded) ||
    (allRows.length === 0 && total > 0);

  // Only the CP tab has filters; the desktop FOS tab offers sort and search only.
  const tags = isCp ? buildActiveFilterTags({ statuses, types, datePreset }) : [];

  // The list is its own scroll container. Since Next replaces the rows rather
  // than extending them, reset to the top so the new page starts at record 1.
  const listRef = useRef(null);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [currentPage, tabValue]);

  // Phone Back closes the open detail page (and any sheet over it) instead of
  // leaving Partners. Hooks run before the early returns below, as they must.
  useBackToClose(Boolean(detailRow), () => {
    setConfirmAction(null);
    onCloseActionSheet();
    onCloseDetail();
  });
  useBackToClose(Boolean(fosDetailRow), () => {
    onCloseActionSheet();
    onCloseFosDetail();
  });

  // FOS detail is a full page too, matching CP. It has no approval workflow,
  // so no confirm sheet and no Approve/Reject bar — actions live in the kebab.
  if (fosDetailRow) {
    return (
      <>
        <FosDetail
          row={fosDetailRow}
          docs={fosDetailDocs}
          onBack={onCloseFosDetail}
          onOverflow={() => onFosAction(fosDetailRow)}
        />
        <ActionSheet
          open={Boolean(actionSheet)}
          title={actionSheet?.title ?? ""}
          items={actionSheet?.items ?? []}
          onSelect={onActionSelect}
          onClose={onCloseActionSheet}
        />
      </>
    );
  }

  // The detail screen is a full overlay — render it instead of the list.
  // Approve is staged through ConfirmSheet. Reject and Suspend open the
  // RejectReason modal straight away, as the desktop menu does; that modal is
  // its own confirmation (reason + Cancel).
  if (detailRow) {
    return (
      <>
        <PartnerDetail
          row={detailRow}
          docs={detailDocs}
          onBack={onCloseDetail}
          onApprove={() => setConfirmAction("approve")}
          onReject={onReject}
          onOverflow={() => onPartnerAction(detailRow)}
          canApprove={canApprove}
          canReject={canReject}
          rejectLabel={rejectLabel}
        />
        <ConfirmSheet
          open={Boolean(confirmAction)}
          action={confirmAction}
          name={detailRow.billingName || detailRow.name || ""}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => {
            setConfirmAction(null);
            onApprove();
          }}
        />
        <ActionSheet
          open={Boolean(actionSheet)}
          title={actionSheet?.title ?? ""}
          items={actionSheet?.items ?? []}
          onSelect={onActionSelect}
          onClose={onCloseActionSheet}
        />
      </>
    );
  }

  return (
    <div className={`partnersMobileRoot ${css.shell}`}>
      <div className={css.shellHeader}>
        <div className={css.shellTitle}>Partners</div>
        <div className={css.shellSubtitle}>
          Manage your Partners and preferences here.
        </div>
      </div>

      <Tabs
        value={tabValue}
        onChange={tabChange}
        sx={{
          borderBottom: "1px solid #EAECF0",
          background: "#fff",
          minHeight: 44,
          "& .MuiTab-root": {
            fontFamily: "Inter-Medium",
            fontSize: 14,
            textTransform: "none",
            minHeight: 44,
          },
        }}
      >
        {(tabs ?? []).map((each) => (
          <Tab key={each} label={each} value={each} />
        ))}
      </Tabs>

      <div className={css.toolbar}>
        <div className={css.searchWrap}>
          {/* SearchBar is uncontrolled; keying it per tab remounts it, so the
              text clears along with the search state the tab change resets. */}
          <SearchBar
            key={isCp ? "cpSearchMobile" : "fosSearchMobile"}
            id={isCp ? "cpSearchMobile" : "fosSearchMobile"}
            placeholder="Search"
            search={isCp ? onSearch : onSearchFos}
            width="100%"
            // SearchBar is styled in rem, and App.css drops the root font to
            // 7px below 767px — its 1.6rem text would render at 11.2px next to
            // these 14px cards. Pin it to px like everything else here.
            sxStyle={SEARCH_SX}
          />
        </div>
        <button
          type="button"
          className={css.iconButton}
          aria-label="Sort"
          onClick={() => setSortOpen(true)}
        >
          <SwapVertIcon sx={{ fontSize: 20 }} />
        </button>
        {isCp && (
          <button
            type="button"
            className={css.iconButton}
            aria-label="Filters"
            onClick={() => setFilterOpen(true)}
          >
            <TuneIcon sx={{ fontSize: 20 }} />
            {tags.length > 0 && <span className={css.filterCount}>{tags.length}</span>}
          </button>
        )}
      </div>

      <div className={css.listMeta}>
        {total} {isCp ? "Partners" : "Fos"}
      </div>

      <div className={css.list} ref={listRef}>
        {tags.length > 0 && (
          <ActiveFilterTags tags={tags} onRemove={onRemoveFilterTag} />
        )}

        {awaitingPage && (
          <div className={css.empty}>
            <div style={{ fontSize: 14 }}>Loading…</div>
          </div>
        )}

        {rows.length === 0 && !awaitingPage && (
          <div className={css.empty}>
            <SearchOffIcon sx={{ fontSize: 40, color: "#D0D5DD" }} />
            <div className={css.emptyTitle}>
              No {isCp ? "partners" : "FOS records"} match
            </div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {isCp ? "Try a different search or filter." : "Try a different search."}
            </div>
          </div>
        )}

        {rows.map((row) =>
          isCp ? (
            <PartnerCard
              key={row.id}
              row={row}
              onOpen={onOpenPartner}
              onAction={onPartnerAction}
            />
          ) : (
            <FosCard
              key={row.id}
              row={row}
              onOpen={onOpenFos}
              onAction={onFosAction}
            />
          )
        )}

        {/* Page-by-page: Next replaces the visible rows with the next page
            rather than extending the list. */}
        {lastPage > 0 && (
          <div className={css.pager}>
            <button
              type="button"
              className={`${css.btn} ${css.btnSecondary}`}
              disabled={currentPage <= 0}
              onClick={() => changePage(currentPage - 1)}
            >
              Previous
            </button>
            <span className={css.pagerInfo}>
              {currentPage + 1} / {lastPage + 1}
            </span>
            <button
              type="button"
              className={`${css.btn} ${css.btnSecondary}`}
              disabled={currentPage >= lastPage}
              onClick={() => changePage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <SortSheet
        open={sortOpen}
        value={sortValue}
        onSelect={onSort}
        onClose={() => setSortOpen(false)}
      />

      <FilterSheet
        open={filterOpen && isCp}
        statuses={statuses}
        types={types}
        datePreset={datePreset}
        customRange={customRange}
        onApply={onApplyFilters}
        onClearAll={onClearFilters}
        onClose={() => setFilterOpen(false)}
      />

      <ActionSheet
        open={Boolean(actionSheet)}
        title={actionSheet?.title ?? ""}
        items={actionSheet?.items ?? []}
        onSelect={onActionSelect}
        onClose={onCloseActionSheet}
      />
    </div>
  );
}
